/**
 * @file facetHelpers.ts
 *
 * @fileoverview Helper functions that will aid the search page function properly.
 * This includes things like getting from url the elements for the graphql query or check what items
 * are selected in a particular facet.
 */

import { FacetInput } from '~/jobiqo/models/graphql';
import {
  Facet,
  FacetConfig,
  FacetValue,
  GeoLocationUrl,
} from '~/jobiqo/models/search';
import { flattenForChildren } from '~/jobiqo/services/taxonomyHelpers';
import { generateQueryStringURL } from '~/jobiqo/services/links';
import { GeoInput } from '~/jobiqo/types/types';
import { ParsedUrlQuery } from 'querystring';
import {
  getSortingFilterOptions,
  SortingFilterOption,
} from '~/jobiqo/components/JobSearch/components/SortingFilter/utils';

// Some parameters that should not be converted to query conditions.
const PARAMS_BYPASS_CHECK = [
  'page',
  'jobId',
  'locale',
  'region',
  'search',
  'geo_location',
  'country',
  'administrative_area_level_1',
  'lat',
  'lon',
  'radius',
  'sorting',
  'last_updated',
  'visibility',
  'resume_contact',
];

// Query parameters matching these patterns should never be included in canonicals.
// Keep this list pattern-based so we can extend it with other tracking params later.
export const CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS: RegExp[] = [
  /^utm_/i,
  /^jobId$/i,
  /^signup$/i,
];

/**
 * Removes query parameters from a query string when their keys match one of the provided patterns.
 */
export function excludeQueryParamsByPattern(
  querystring: string,
  excludePatterns: RegExp[],
): string {
  const searchParams = new URLSearchParams(querystring);
  const paramsToDelete = Array.from(searchParams.keys()).filter((key) =>
    excludePatterns.some((pattern) => pattern.test(key)),
  );

  if (paramsToDelete.length === 0) {
    return querystring;
  }

  paramsToDelete.forEach((param) => searchParams.delete(param));

  return searchParams.toString();
}

/**
 * Removes query parameters from a full path when their keys match one of the provided patterns.
 */
export function excludeCanonicalPathQueryParamsByPattern(
  path: string,
  excludePatterns: RegExp[],
): string {
  const querystringStart = path.indexOf('?');

  if (querystringStart === -1) {
    return path;
  }

  const cleanPath = path.substring(0, querystringStart);
  const querystring = excludeQueryParamsByPattern(
    path.substring(querystringStart + 1),
    excludePatterns,
  );

  return `${cleanPath}${querystring ? `?${querystring}` : ''}`;
}

/**
 * Check if facet is selected.
 *
 * Based on the current route and query parameters check if a facet is selected
 * and pass to the facets component via props.
 *
 * @param {any} querystring
 *   The querystring object that comes from nextjs.
 *
 * @param {facet} facet
 *  The facet to check for.
 */
export const getFilterSelectedItems = (
  querystring: any,
  facet: Facet,
): string[] => {
  return flattenForChildren(facet.values)
    .filter((value) => {
      if (querystring[facet.name]) {
        return querystring[facet.name].includes(value.filter.toString());
      } else {
        return false;
      }
    })
    .map((item) => {
      return item.filter;
    });
};

/**
 * Calculate the query variables.
 *
 * Based on the current route and query parameters calculate the graphql query
 * options dynamically.
 */
export const getQueryVariables = (
  querystring: any,
  itemsPerPage: number,
  searchFacets: FacetConfig[],
  language,
  sortingFilterOptions?: SortingFilterOption[],
) => {
  let groups: any[] = [];
  let geoLocation: GeoInput | null = null;
  let fulltext: { keys: string } = {
    keys: '',
  };
  let sorts: Array<{ field: string; value: string }> = [];
  const SORTING_FILTER_OPTIONS: SortingFilterOption[] =
    sortingFilterOptions ?? getSortingFilterOptions();

  // Geo GeoInfo from the url.
  if (querystring.geo_location) {
    // In case geoLocation is an array we need to convert it to string.
    if (Array.isArray(querystring.geo_location)) {
      querystring.geo_location = querystring.geo_location.join(', ');
    }
    geoLocation = {
      label: querystring.geo_location,
      country: querystring.country,
      administrativeArea: querystring[querystring.locationType],
      lat: Number(querystring.lat),
      lon: Number(querystring.lon),
      radius: Number(querystring.radius),
      locationType: querystring.locationType ?? '',
      termId: Number(querystring.termId) ?? null,
    };
  }

  // If we have a sorting in the url we need to add it to the query.
  if (querystring.sorting) {
    const sortingFilterOption = SORTING_FILTER_OPTIONS.find(
      (item) => item.code === querystring.sorting,
    );
    if (sortingFilterOption && sortingFilterOption.code !== 'relevancy') {
      sorts = [
        {
          field: sortingFilterOption.field,
          value: sortingFilterOption.value,
        },
      ];
    } else {
      sorts = [];
    }
  }

  // Prepare conditions based on the query string.
  if (querystring) {
    Object.keys(querystring).forEach((key) => {
      groups = getQueryConditions(querystring, searchFacets);
      // Check for fulltext search in the url.
      if (key === 'search') {
        fulltext = {
          keys: querystring[key],
        };
      }
    });
  }

  // Handle "Last Updated" time-range filter for resume search.
  // Sends the time-range code (e.g. "today", "last_week") as-is —
  // the backend event subscriber converts it to a Unix timestamp.
  if (querystring.last_updated && querystring.last_updated !== 'show_all') {
    groups.push({
      conjunction: 'OR',
      conditions: [
        {
          name: 'changed',
          value: querystring.last_updated as string,
          operator: '>=',
        },
      ],
    });
  }

  // Handle visibility filter for resume search.
  if (querystring.visibility === 'hide_revealed') {
    groups.push({
      conjunction: 'OR',
      conditions: [
        {
          name: 'resume_contact',
          value: 'hide_revealed',
          operator: '=',
        },
      ],
    });
  }

  // Handle resume contact filter (Pending/Approved requests).
  if (querystring.resume_contact) {
    const values = Array.isArray(querystring.resume_contact)
      ? querystring.resume_contact
      : (querystring.resume_contact as string).split(',');
    const conditions = values
      .filter((v) => v === 'pending' || v === 'approved')
      .map((value) => ({
        name: 'resume_contact',
        value,
        operator: '=',
      }));
    if (conditions.length > 0) {
      groups.push({
        conjunction: 'OR',
        conditions,
      });
    }
  }

  const facetSolr: FacetInput[] = searchFacets
    .filter((facet) => facet) // Remove hidden / null facets.
    .map((facetTerm: FacetConfig) => {
      return {
        field: facetTerm.fieldName,
        limit: facetTerm.limit ? facetTerm.limit : 100,
        operator: facetTerm.operator,
        min_count: 1,
        missing: false,
      };
    });

  // return the graphql variables expected by the
  // apollo query in the render() method.
  return {
    language,
    facets: facetSolr,
    condition_group: {
      conjunction: 'AND',
      groups,
    },
    fulltext,
    geoLocation: geoLocation ? geoLocation : undefined,
    range: {
      first:
        querystring.page && querystring.page > 0
          ? (querystring.page - 1) * itemsPerPage
          : 0,
      last: itemsPerPage,
    },
    sorts: sorts.length > 0 ? sorts : undefined,
  };
};

/**
 * Check if a key is in the facets.
 * @param searchFacets
 *   The search facets.
 * @param key
 *   The key to check.
 */
const checkKeyInFacets = (
  searchFacets: FacetConfig[],
  key: string,
): boolean => {
  return searchFacets.some((facet) => facet && facet.fieldName === key);
};

/**
 * Gets the conditions for search API based on the current
 * querystring.
 *
 * @param {string} querystring
 *   The current querystring
 * @param {FacetConfig[]} searchFacets
 */
export const getQueryConditions = (
  querystring,
  searchFacets: FacetConfig[] = [],
) => {
  // Prepare conditions based on the query string.
  let groups: any[] = [];
  if (querystring) {
    Object.keys(querystring).forEach((key) => {
      // page key is for the pagination so ignore that
      // @todo improve this.
      if (
        !PARAMS_BYPASS_CHECK.includes(key) &&
        checkKeyInFacets(searchFacets, key)
      ) {
        let conditions: any[] = [];

        // Collect values from querystring for this key.
        let valsQuery: string[] = [];
        if (Array.isArray(querystring[key])) {
          // If we have more than one value selected keep it as is (e.g. [1,2]).
          valsQuery = querystring[key] as string[];
        } else if (querystring[key]) {
          // If we have only one value selected add it to an array (e.g. [1]).
          valsQuery = [decodeURIComponent(querystring[key] as string)];
        }

        // Foreach filter we have set a new condition.
        valsQuery.forEach((item) => {
          // For each one a condition group.
          let conditionObject: any = { operator: '=', name: key };

          // Split items by comma and handle as separate values if they are numbers
          const splitItems = item.split(',');
          if (
            splitItems.length > 1 &&
            splitItems.every((element) => !isNaN(Number(element)))
          ) {
            splitItems.forEach((itemValue) => {
              let individualCondition = {
                ...conditionObject,
                value: itemValue,
              };
              // Remove duplicates
              if (
                !conditions.some(
                  (arrayCondition) =>
                    arrayCondition.operator === individualCondition.operator &&
                    arrayCondition.name === individualCondition.name &&
                    arrayCondition.value === individualCondition.value,
                )
              ) {
                conditions.push(individualCondition);
              }
            });
          } else {
            conditionObject = { ...conditionObject, value: item };
            // Remove duplicates
            if (
              !conditions.some(
                (arrayCondition) =>
                  arrayCondition.operator === conditionObject.operator &&
                  arrayCondition.name === conditionObject.name &&
                  arrayCondition.value === conditionObject.value,
              )
            ) {
              conditions.push(conditionObject);
            }
          }
        });

        // If the filter is facet we include the facet tag.
        const conditionGroup = {
          conjunction: 'OR',
          tags: searchFacets.some((facet) => facet && facet.fieldName === key)
            ? [`facet:${key}`]
            : [],
          conditions,
        };
        groups.push(conditionGroup);
      }
    });
  }
  return groups;
};

/**
 * Process the facet to include proper title and names.
 * We only index the term id's in solr.
 */
export const processFacets = (
  facets: readonly Facet[],
  jobSearchFacets: FacetConfig[],
): Facet[] => {
  if (!facets) {
    return [];
  }

  // Create a map from facet names to their configurations.
  // Make sure the facet is no null (hidden).
  const facetConfigMap = new Map(
    jobSearchFacets
      .filter((facetConf) => facetConf?.fieldName)
      .map((facetConfig) => [facetConfig.fieldName, facetConfig]),
  );

  // Create a copy of the facets array and sort the copy.
  const sortedFacets = [...facets].sort((a, b) => {
    const facetA = facetConfigMap.get(a.name);
    const facetB = facetConfigMap.get(b.name);

    if (facetA && facetB) {
      return (
        (facetA?.order ?? jobSearchFacets.indexOf(facetA)) -
        (facetB?.order ?? jobSearchFacets.indexOf(facetB))
      );
    }

    return 0;
  });

  // Filter and map the facets in one pass using reduce.
  return sortedFacets.reduce((acc: Facet[], facet: Facet) => {
    const facetInfo = facetConfigMap.get(facet.name);

    if (facetInfo) {
      acc.push({
        ...facet,
        title: facetInfo.title,
        multiple: facetInfo.multiple,
      });
    }

    return acc;
  }, []);
};

/**
 * Return the facet link with appropriate facet item appended to the url.
 *
 * @param {querystring} any
 *   The querystring object.
 * @param {facetName} string
 *   The facet name (key) to add to the url.
 * @param {FacetValue} facetItem
 *   The facet item to add to the url.
 *
 * @returns string
 *   The link to render in the facet.
 */
export const getFacetLink = (
  querystring: any,
  facetName: string,
  facetItem: FacetValue,
): string => {
  const newQuery = {
    ...querystring,
    [facetName]: facetItem.filter,
  };
  const newUrl = generateQueryStringURL(newQuery);
  return `?${newUrl}`;
};

/**
 * Returns the clean part of a url.
 *
 * @param {string} path
 *  The full url.
 */
export const getCleanUrlPart = (path: string): string => {
  if (path.indexOf('?') > 0) {
    return `${path.substring(0, path.indexOf('?'))}`;
  } else {
    return `${path}`;
  }
};

/**
 * Returns the URL without fragments.
 *
 * @param {string} path
 *  The full url.
 */
export const getUrlWithoutFragment = (path: string): string => {
  if (path.indexOf('#') > 0) {
    return `${path.substring(0, path.indexOf('#'))}`;
  } else {
    return `${path}`;
  }
};

/**
 * Ads or replaces an item in the url.
 *
 * Taken a url this function will return the new url (string)
 * with the item added or replaced. It will check if the current url has already an item in it.
 * If it has it will replace (either with ? or & depending on position).
 * If the url has nothing it will just append it in the end.
 *
 * @param {string} path
 *  The current path
 * @param {itemId} string
 *  The item we want to replace (key e.g. page -> ?page=xyz)
 * @param {string} itemNewValue
 *  The new value to add or replace (e.g. 2 for &page=2)
 *
 * @returns {string}
 *   The new URL string.
 */
export const addOrReplaceGETParamItemInUrlString = (
  path: string,
  itemId: string,
  itemNewValue: string,
  filtersToExclude?: string[],
) => {
  const pathHasParams = path.indexOf('?') >= 0;
  let searchParams = new URLSearchParams(
    pathHasParams ? path.split('?')[1] : '',
  );

  // We want to clear some filters when pagination happens.
  if (filtersToExclude) {
    filtersToExclude.forEach((filterToExclude) => {
      searchParams?.delete(filterToExclude);
    });
  }

  searchParams?.set(itemId, itemNewValue);

  return `${
    pathHasParams ? path.split('?')[0] : path
  }?${searchParams?.toString()}`;
};

/**
 * Returns path without query params.
 *
 * @param {string} path
 *  The current path.
 * @param {string[]} excludeParams
 *  The params to exclude from path.
 * @returns
 *  Path without excludeParams argument.
 */
export function excludeQueryParams(
  path: string,
  excludeParams: string[],
): string {
  // The path has no query params.
  if (path.indexOf('?') === -1) {
    return path;
  }

  const pathParts = path.split('?');
  const searchParams = new URLSearchParams(pathParts[1]);

  excludeParams.forEach((param) => searchParams.delete(param));

  return `${pathParts[0]}${
    Array.from(searchParams.values()).length > 0
      ? '?' + searchParams.toString()
      : ''
  }`;
}

/**
 * Convers the GeoInput information into parameters for the URL.
 *
 * Use the same keys we used in jobiqo7 projects to make migration easier
 * and make sure that keys from GeoInput type (coming from graphql) are mapped to
 * the correct url parameters.
 *
 * @param {string} value
 *  The value searched in location search.
 */
export const getGeoLocationUrlObjectFromGeoInput = (
  value: GeoInput,
): GeoLocationUrl => {
  const geoLocationUrl: GeoLocationUrl = {};
  Object.keys(value as GeoInput).forEach((key) => {
    switch (key) {
      case 'label':
        geoLocationUrl.geo_location = value[key] as any;
        return;
      case 'locationType':
        // In order to have
        // e.g. &administrative_area_level_1=California.
        geoLocationUrl[value[key]] = value['label'] as any;
        geoLocationUrl[key] = value[key] as any;
        return;
      case 'administrativeArea':
        geoLocationUrl[value['locationType']] = value[key] as any;

        return;
      default:
        geoLocationUrl[key] = value[key] as any;
    }
  });
  // Remove __typename and path from the geoLocationUrl object.
  delete geoLocationUrl.__typename;
  delete geoLocationUrl.path;
  return geoLocationUrl;
};

/**
 * Convers the GeoInput information into parameters for the URL.
 *
 * Use the same keys we used in jobiqo7 projects to make migration easier
 * and make sure that keys from GeoInput type (coming from graphql) are mapped to
 * the correct url parameters.
 *
 * @param {string} value
 *  The value searched in location search.
 */
export const getGeoInputFromUrl = (querystring: ParsedUrlQuery): GeoInput => {
  const geoLocationUrl: any = {};
  Object.keys(querystring).forEach((key) => {
    switch (key) {
      case 'geo_location':
        geoLocationUrl.label = querystring[key] as any;
        return;
      case 'administrative_area_level_1':
        geoLocationUrl.administrativeArea = querystring[key] as any;
        return;
      case 'country':
        geoLocationUrl.country = querystring[key] as any;
        return;
      case 'locationType':
        geoLocationUrl.locationType = querystring[key] as any;
        return;
      case 'location':
        geoLocationUrl.location = querystring[key] as any;
        return;
      case 'lat':
        geoLocationUrl.lat = querystring[key] as any;
        return;
      case 'lon':
        geoLocationUrl.lon = querystring[key] as any;
        return;
      case 'radius':
        geoLocationUrl.radius = querystring[key] as any;
        return;
      default:
        return;
    }
  });
  return geoLocationUrl;
};

/**
 * Gets all the keys that are used in URLs to specify a location.
 */
export const getLocationUrlKeys = (): string[] => {
  return [
    'geo_location',
    'administrative_area_level_1',
    'country',
    'locationType',
    'location',
    'locality',
    'lat',
    'lon',
    'radius',
  ];
};

export function removeGeoLocationFromQueryString(querystring: any): any {
  const geoLocationKeys = getLocationUrlKeys();
  // Return a new object without the geo location keys
  return Object.fromEntries(
    Object.entries(querystring).filter(
      ([key]) => !geoLocationKeys.includes(key),
    ),
  );
}

/**
 * Orders the segments in the URL based on the order of facets in config. IF a URL has the following structure
 * /jobs/computer-software/full-time it will return all the information about each of the URL parts such as :
 *
 * [
 *  {
 *    vocabulary: "employment_type",
 *    label: "Full-time",
 *    filter: "1234",
 *    alias: "full-time"
 *  },
 *  {
 *    vocabulary: "occupations",
 *    label: "Computer Software",
 *    filter: "5856",
 *    alias: "computer-software"
 *  }
 * ]
 *
 * @param path
 *  The path of the page.
 * @param searchItems
 *  The search items.
 * @param searchPage
 *  The search page.
 */
export function getOrderedURLSegments(
  path: string,
  searchItems: any,
  searchPage: string,
  facetsOrder: string[],
) {
  const cleanURlSegments = getCleanUrlPart(path);
  const urlTaxonomies = cleanURlSegments
    .replace(`${searchPage}/`, '')
    .split('/');
  let urlParts = urlTaxonomies.reduce((acc: any, urlTax) => {
    // Get the facet vocabulary and the desired facet value. The desired facet value is the one that matches
    // the alias in the URL. We need to check also children facets.
    let [facetVocabulary, desiredFacet] = extractFacetValueByAlias(
      searchItems?.facets,
      urlTax,
    );

    if (desiredFacet && facetVocabulary) {
      acc.push({
        vocabulary: facetVocabulary,
        label: desiredFacet.label,
        filter: desiredFacet?.filter,
        alias: desiredFacet?.alias,
      });
    }

    // Order the acc array based on the property filter. USe the facetsOrder which is an array
    // already in the right order.
    acc.sort((a: any, b: any) => {
      return (
        facetsOrder.indexOf(a.vocabulary) - facetsOrder.indexOf(b.vocabulary)
      );
    });

    return acc;
  }, []);
  return urlParts;
}

/**
 * Recursive function to find a facet by its alias in a given facet value object. It searches in the children as well.
 *
 * @param facetValue
 *  The facet value object to search in.
 * @param urlTax
 *  The alias of the facet to find.
 * @returns
 *  The found facet object, or undefined if not found.
 */
function findFacetByAlias(facetValue: any, urlTax: string): any | undefined {
  let desiredFacet: any | undefined;

  if (facetValue.alias === urlTax) {
    desiredFacet = facetValue;
  }

  if (facetValue.children && facetValue.children.length > 0) {
    for (const facetValueChild of facetValue.children) {
      const wantedValue = findFacetByAlias(facetValueChild, urlTax);
      if (wantedValue) {
        desiredFacet = wantedValue;
        break;
      }
    }
  }

  return desiredFacet;
}

/**
 * Extracts the facet vocabulary and facet value by alias from the given facets.
 *
 * @param facets
 *  The array of facets.
 * @param facetAlias
 *  The alias of the desired facet value.
 * @returns
 *  An array containing the facet vocabulary and the desired facet value.
 */
function extractFacetValueByAlias(facets: any, facetAlias: string) {
  let facetVocabulary: string | undefined;
  let desiredFacet: any;
  if (facets) {
    for (const facet of facets) {
      facetVocabulary = facet.name;
      for (const facetValues of facet.values) {
        desiredFacet = findFacetByAlias(facetValues, facetAlias);
        if (desiredFacet) {
          break;
        }
      }
      if (desiredFacet) {
        break;
      }
    }
  }

  return [facetVocabulary, desiredFacet];
}

/**
 * Replaces the segments in the URL based on the order of facets in config.
 *
 * IF a URL has the following structure /jobs/computer-software/full-time it will return the URL
 * according to the right config order which would be by default /jobs/full-time/computer-software.
 *
 * @param path
 *  The path of the page.
 * @param searchItems
 *  The search items (jobs or organization SOLR Documents).
 * @param searchPage
 *  The search page.
 * @param facetsOrder
 *  The order of facets in config.
 * @returns
 *  The new URL with the segments in the right order.
 */
export function replaceSegmentsWithRightOrder(
  path: string,
  searchItems: any,
  searchPage: string,
  facetsOrder: string[],
): string {
  const pathWithoutCanonicalExcludedQueryParams = excludeCanonicalPathQueryParamsByPattern(
    path,
    CANONICAL_QUERY_PARAM_EXCLUDE_PATTERNS,
  );
  const cleanURlSegments = getCleanUrlPart(
    pathWithoutCanonicalExcludedQueryParams,
  );
  const originalFullCleanUrl = cleanURlSegments.split('/');
  const querystring =
    pathWithoutCanonicalExcludedQueryParams.indexOf('?') >= 0
      ? pathWithoutCanonicalExcludedQueryParams.split('?')[1]
      : '';

  const orderedUrlParts = getOrderedURLSegments(
    path,
    searchItems,
    searchPage,
    facetsOrder,
  );
  const orderedTaxonomies = orderedUrlParts.map((part) => part.alias);
  // We only do something (reorder the url) if we know the order of all elements in the URL.
  // Otherwise this can lead to problematic canonicals.
  // Diff is 2 because it includes "" and "jobs" which are parts of the url /jobs/computer-software/full-time
  // has 4 parts : "", "jobs", "computer-software", "full-time"
  const diff = originalFullCleanUrl.length - orderedTaxonomies.length;

  if (diff === 2) {
    // We know all the segments in the original url are present in
    // the orderedUrlParts array so we can map the original url to the
    // ordered url.
    const newURLParts = originalFullCleanUrl.map((segment, index) => {
      if (index < diff) {
        return segment;
      }
      const { alias } = orderedUrlParts[index - diff];
      return alias;
    });

    return `${newURLParts.join('/')}${querystring ? `?${querystring}` : ''}`;
  } else {
    return `${cleanURlSegments}${querystring ? `?${querystring}` : ''}`;
  }
}

/**
 * Translates segment of a job search page into their respective languages (where translations are available).
 *
 * @param path
 *  The path of the page.
 * @param searchItems
 *  The search items.
 * @param searchPage
 *  The search page.
 */
export function getTranslatedSegments(
  path: string,
  searchItems: any,
  searchPage: string,
) {
  const cleanURlSegments = getCleanUrlPart(path);
  const urlTaxonomies = cleanURlSegments
    .replace(`${searchPage}/`, '')
    .split('/');
  let translatedUrlParts = urlTaxonomies.reduce((acc: any, urlTax) => {
    let [facetVocabulary, desiredFacet] = extractFacetValueByAlias(
      searchItems?.facets,
      urlTax,
    );

    if (desiredFacet && facetVocabulary) {
      if (desiredFacet?.aliasTranslation) {
        desiredFacet.aliasTranslation.forEach((ut) => {
          acc.push({
            taxonomy: facetVocabulary,
            originalSegment: urlTax,
            segment: ut?.alias,
            language: ut?.langcode,
          });
        });
      }
    }
    return acc;
  }, []);
  return translatedUrlParts;
}

/**
 * Helper function to return translated search path based on given values.
 *
 * @param path
 *  The search path for which we want to get translation.
 * @param locale
 *  The language in which we want the translated path.
 * @param translatedUrlSegments
 *  The translated URL segments.
 * @returns
 *  The translated job search path.
 */
export function getTranslatedSearchPath(
  path: string,
  locale: string,
  translatedUrlSegments: any,
): string {
  const newSegs = translatedUrlSegments.filter((i) => i.language === locale);
  let newUrl = path;
  newSegs.forEach((newSeg) => {
    newUrl = newUrl.replace(`/${newSeg.originalSegment}`, `/${newSeg.segment}`);
  });

  return newUrl;
}
